import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  addEdge,
  Background,
  BezierEdge,
  Controls,
  ReactFlow,
  useEdgesState,
  useNodesState,
  useReactFlow
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import { useDnD } from './dnDContext';
import MDBox from 'components/MDBox';
import {
  assistantNodeTypes,
  isValidConnectionNodes
} from 'pages/assistants/builder/components/utils';
import { useFlow } from 'pages/assistants/builder/components/flowContext';
import { generateUUID } from 'utils/cryptoUtils';
import NodeFormDetailsDrawer from 'pages/assistants/builder/components/nodeFormDetailsDrawer';

const getId = () => {
  return generateUUID();
};

function cleanNodes(nodes) {
  if (!nodes) return [];

  return nodes.map((node) => {
    // eslint-disable-next-line no-unused-vars
    const { measured, dragging, selected, ...cleanNode } = node;
    return cleanNode;
  });
}

// Custom equality checker for nodes and edges to prevent unnecessary re-renders
const areNodesEqual = (prevNodes, nextNodes) => {
  if (prevNodes === nextNodes) return true;
  if (prevNodes.length !== nextNodes.length) return false;

  return isEqual(
    prevNodes.map((n) => ({
      id: n.id,
      type: n.type,
      position: n.position,
      data: n.data
    })),
    nextNodes.map((n) => ({
      id: n.id,
      type: n.type,
      position: n.position,
      data: n.data
    }))
  );
};

const areEdgesEqual = (prevEdges, nextEdges) => {
  if (prevEdges === nextEdges) return true;
  if (prevEdges.length !== nextEdges.length) return false;

  return isEqual(
    prevEdges.map((e) => ({
      id: e.id,
      source: e.source,
      target: e.target,
      sourceHandle: e.sourceHandle,
      targetHandle: e.targetHandle,
      type: e.type
    })),
    nextEdges.map((e) => ({
      id: e.id,
      source: e.source,
      target: e.target,
      sourceHandle: e.sourceHandle,
      targetHandle: e.targetHandle,
      type: e.type
    }))
  );
};

export const FlowCanvas = memo(
  ({
    flow = {
      nodes: [],
      edges: [],
      viewport: { x: 0, y: 0, zoom: 1 }
    }
  }) => {
    const reactFlowWrapper = useRef(null);
    const [initialNodes] = useState(() => cleanNodes(flow?.nodes) || []);
    const [initialEdges] = useState(() => flow?.edges || []);
    const [displayNodeDrawer, setDisplayNodeDrawer] = useState(false);

    const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
    const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

    const [currentNode, setCurrentNode] = useState(null);

    const [rfInstance, setRfInstance] = useState(null);
    const { screenToFlowPosition, setViewport, getNode } = useReactFlow();
    const { node } = useDnD();
    const { registerMethod } = useFlow();
    const viewportInitialized = useRef(false);

    const isValidConnection = useCallback(
      (connection) => {
        return isValidConnectionNodes(nodes, connection);
      },
      [nodes]
    );

    const edgeTypes = useMemo(
      () => ({
        bezier: BezierEdge
      }),
      []
    );

    useEffect(() => {
      if (flow?.viewport && rfInstance && !viewportInitialized.current) {
        const restoreFlow = () => {
          setViewport({
            x: flow.viewport.x || 0,
            y: flow.viewport.y || 0,
            zoom: flow.viewport.zoom || 1
          });
          viewportInitialized.current = true;
        };

        // Schedule the viewport update on the next frame to avoid React 18 synchronization issues
        requestAnimationFrame(restoreFlow);
      }
    }, [flow, rfInstance, setViewport]);

    // Stable identity for node change handler
    const handleNodeChange = useCallback(
      (newNode) => {
        if (!newNode || !newNode.id) return;

        setNodes((currentNodes) => {
          const updatedNodes = currentNodes.map((node) => {
            if (node.id === newNode.id) {
              // Create a new node with updated data
              return {
                ...node,
                data: { ...newNode.data }
              };
            }
            return node;
          });

          // Only return updated array if something changed
          const hasChanged = !areNodesEqual(currentNodes, updatedNodes);
          return hasChanged ? updatedNodes : currentNodes;
        });

        setCurrentNode(null);
      },
      [setNodes]
    );

    const handleEditNode = useCallback(
      (nodeId) => {
        const node = getNode(nodeId);

        if (!node) {
          console.warn(`Node with ID ${nodeId} not found.`);
          return;
        }

        setCurrentNode(node);
        setDisplayNodeDrawer(true);
      },
      [getNode]
    );

    const handleDeleteNode = useCallback(
      (nodeId) => {
        setNodes((currentNodes) => currentNodes.filter((node) => node.id !== nodeId));

        setEdges((currentEdges) =>
          currentEdges.filter((edge) => edge.source !== nodeId && edge.target !== nodeId)
        );
      },
      [setNodes, setEdges]
    );

    // Memoized node types with stable change handler
    const nodeTypes = useMemo(
      () => assistantNodeTypes(handleEditNode, handleDeleteNode),
      [handleEditNode, handleDeleteNode]
    );

    // Stable connection handler
    const onConnect = useCallback(
      (params) => {
        const edgeParams = {
          ...params,
          type: 'bezier'
        };

        setEdges((currentEdges) => {
          const newEdges = addEdge(edgeParams, currentEdges);

          // Only update if edges actually changed
          const hasChanged = !areEdgesEqual(currentEdges, newEdges);
          return hasChanged ? newEdges : currentEdges;
        });
      },
      [setEdges]
    );

    // Stable drag over handler
    const onDragOver = useCallback((event) => {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
    }, []);

    // Optimize drop handler
    const onDrop = useCallback(
      (event) => {
        event.preventDefault();
        if (!node.type) return;

        const position = screenToFlowPosition({
          x: event.clientX,
          y: event.clientY
        });

        // Early exit conditions
        if (node.type === 'userInput' && nodes.some((node) => node.type === 'userInput')) {
          return;
        }

        if (node.type === 'userOutput' && nodes.some((node) => node.type === 'userOutput')) {
          return;
        }

        let displayDrawer = true;

        const nodeData = (() => {
          switch (node.type) {
            case 'prompt':
              displayDrawer = false;
              return { label: 'Prompt', code: '', content: '', icon: 'speaker_notes' };
            case 'userInput':
              return { label: 'Entrée', icon: 'person_icon' };
            case 'userOutput':
              displayDrawer = false;
              return { label: 'Sortie', icon: 'exit_to_app_rounded_icon' };
            case 'mailOutput':
              return { label: 'Envoi email', type: 'mailOutput', icon: 'mail_icon' };
            case 'mailInput':
              return { label: 'Email', type: 'mailInput', icon: 'mail_icon' };
            case 'model':
              return { label: 'Modèle', icon: 'smart_toy_icon' };
            case 'knowledgeBase':
              displayDrawer = false;
              return { label: 'Base de connaissance', icon: 'school' };
            case 'advisor':
              return { label: 'Advisor', code: '', type: '', prompt: '', icon: 'auto_awesome' };
            case 'rewriteAdvisor':
              return {
                label: 'Réécriture',
                code: '',
                type: 'rewrite',
                prompt: '',
                icon: 'draw_icon'
              };
            case 'classificationProcessor':
              return {
                label: 'Classification',
                code: '',
                type: node.type,
                prompt: '',
                icon: 'clear_all_icon'
              };
            case 'memoryAdvisor':
              displayDrawer = false;
              return {
                label: 'Mémoire',
                code: '',
                type: 'memory',
                prompt: '',
                icon: 'sd_card_icon'
              };
            case 'documentAdvisor':
              return {
                label: 'Documents',
                code: '',
                type: 'document',
                prompt: '',
                icon: 'content_paste_search_icon'
              };
            case 'logAdvisor':
              displayDrawer = false;
              return {
                label: 'Journalisation',
                code: '',
                type: 'log',
                prompt: '',
                icon: 'menu_book_icon'
              };
            case 'account':
              return {
                label: 'Compte de connexion',
                code: '',
                type: 'account',
                prompt: '',
                icon: 'lock_icon'
              };
            case 'tools':
              displayDrawer = false;
              return {
                label: `${node.label}`,
                code: '',
                type: 'tools',
                toolsId: `${node.toolsId}`,
                icon: `${node.icon}`
              };
            default:
              return { label: `${node.type}` };
          }
        })();

        const newNode = {
          id: `${node.type}_${getId()}`,
          type: node.type,
          position,
          data: nodeData
        };

        // Add new node
        setNodes((nds) => [...nds, newNode]);

        if (displayDrawer) {
          setCurrentNode(newNode);
          setDisplayNodeDrawer(true);
        }
      },
      [screenToFlowPosition, setNodes, node, nodes]
    );

    // Stable saveFlow function
    const saveFlow = useCallback(() => {
      if (rfInstance) {
        return rfInstance.toObject();
      }
      return null;
    }, [rfInstance]);

    // Register a saveFlow method with cleanup
    useEffect(() => {
      registerMethod('saveFlow', saveFlow);

      return () => {
        registerMethod('saveFlow', null);
      };
    }, [registerMethod, saveFlow]);

    return (
      <MDBox flex={1} sx={{ display: 'flex', height: '100%' }}>
        <MDBox ref={reactFlowWrapper} sx={{ flex: 1, position: 'relative', height: '100%' }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onDrop={onDrop}
            edgeTypes={edgeTypes}
            onDragOver={onDragOver}
            nodeTypes={nodeTypes}
            onInit={setRfInstance}
            defaultEdgeOptions={{
              type: 'bezier',
              animated: true
            }}
            fitView
            isValidConnection={isValidConnection}
            proOptions={{ hideAttribution: true }}
            style={{ backgroundColor: '#F7F9FB', width: '100%', height: '100%' }}
          >
            <Controls />
            <Background color="#aaa" gap={16} />
          </ReactFlow>
        </MDBox>
        {currentNode && (
          <NodeFormDetailsDrawer
            node={currentNode}
            onNodeChanged={handleNodeChange}
            drawerOpen={displayNodeDrawer}
            setDrawerOpen={setDisplayNodeDrawer}
          />
        )}
      </MDBox>
    );
  },
  (prevProps, nextProps) => {
    // Custom React.memo comparison function
    if (prevProps === nextProps) return true;

    // If both props are null or undefined
    if (!prevProps?.flow && !nextProps?.flow) return true;

    // If one is null but not the other
    if (!prevProps?.flow || !nextProps?.flow) return false;

    // Compare basic flow properties - but ignore deep changes to nodes/edges
    // We handle those separately with useNodesState and useEdgesState
    const prevViewport = prevProps.flow.viewport || {};
    const nextViewport = nextProps.flow.viewport || {};

    return (
      isEqual(prevViewport, nextViewport) &&
      prevProps.flow.nodes?.length === nextProps.flow.nodes?.length &&
      prevProps.flow.edges?.length === nextProps.flow.edges?.length
    );
  }
);

FlowCanvas.propTypes = {
  flow: PropTypes.shape({
    nodes: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
        position: PropTypes.shape({
          x: PropTypes.number.isRequired,
          y: PropTypes.number.isRequired
        }).isRequired,
        data: PropTypes.object.isRequired
      })
    ),
    edges: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        source: PropTypes.string.isRequired,
        target: PropTypes.string.isRequired,
        sourceHandle: PropTypes.string,
        targetHandle: PropTypes.string,
        type: PropTypes.string
      })
    ),
    viewport: PropTypes.shape({
      x: PropTypes.number,
      y: PropTypes.number,
      zoom: PropTypes.number
    })
  })
};

// Enable Why Did You Render for FlowCanvas
FlowCanvas.whyDidYouRender = true;
